import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminDashboard from "../AdminDashboard/admindash";
import {
  Edit3,
  MapPin,
  Globe,
  FileText,
  Tag,
  Image as ImageIcon,
  ArrowRight,
  Eye,
  Loader2,
  RefreshCcw
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function Updatedest() {
  const [inputs, setInputs] = useState({
    destinationName: "",
    country: "",
    description: "",
    category: "",
    image: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchhandler = async () => {
      try {
        const res = await axios.get(`${API_URL}/destinations/${id}`);
        const obj = res.data.destination || res.data.destA || res.data;
        setInputs(obj);
      } catch (err) {
        console.error("GET failed:", err);
        toast.error("Failed to load destination details.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchhandler();
  }, [id]);

  const handleChange = (e) => {
    setInputs((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.put(`${API_URL}/destinations/${id}`, inputs);
      toast.success("Destination updated successfully!");
      setTimeout(() => navigate('/destinationview'), 1500);
    } catch (err) {
      console.error("PUT failed:", err);
      toast.error("Failed to update destination. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminDashboard>
        <div className="h-96 flex flex-col items-center justify-center gap-4 text-sky-600">
          <Loader2 size={40} className="animate-spin" />
          <p className="font-black uppercase tracking-widest text-xs">Loading Destination Data...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="font-sans">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
          <div>
            <h1 className="text-2xl font-black text-sky-950 tracking-tight flex items-center gap-2">
              <RefreshCcw className="text-sky-500" size={24} />
              Update Destination
            </h1>
            <p className="text-sm text-gray-400 font-bold mt-1">Refine and polish this TravelAura location.</p>
          </div>
          <button
            onClick={() => navigate("/destinationview")}
            className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-sky-600 hover:text-sky-700 transition-colors bg-sky-50 px-4 py-2 rounded-xl border border-sky-100"
          >
            Cancel & Return <ArrowRight size={14} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* Edit Form Section */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Destination Name */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Destination Name</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                  <input
                    type="text"
                    name="destinationName"
                    value={inputs.destinationName}
                    onChange={handleChange}
                    placeholder="Enter destination name"
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Province Dropdown */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Province</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                    <select
                      name="country"
                      value={inputs.country}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                    >
                      <option value="" disabled>Select Province</option>
                      <option value="Central Province">Central Province</option>
                      <option value="Eastern Province">Eastern Province</option>
                      <option value="North Central Province">North Central Province</option>
                      <option value="Northern Province">Northern Province</option>
                      <option value="North Western Province">North Western Province</option>
                      <option value="Sabaragamuwa Province">Sabaragamuwa Province</option>
                      <option value="Southern Province">Southern Province</option>
                      <option value="Uva Province">Uva Province</option>
                      <option value="Western Province">Western Province</option>
                    </select>
                  </div>
                </div>

                {/* Category Dropdown */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Category</label>
                  <div className="relative">
                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                    <select
                      name="category"
                      value={inputs.category}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                    >
                      <option value="" disabled>Select Category</option>
                      <option value="Beach">Beach</option>
                      <option value="Historical">Historical</option>
                      <option value="Adventure">Adventure</option>
                      <option value="Wildlife">Wildlife</option>
                      <option value="Cultural">Cultural</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Description</label>
                <div className="relative">
                  <FileText className="absolute left-4 top-4 text-sky-400" size={18} />
                  <textarea
                    name="description"
                    value={inputs.description}
                    onChange={handleChange}
                    placeholder="Enter a compelling description..."
                    required
                    rows="4"
                    className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                  ></textarea>
                </div>
              </div>

              {/* Image URL */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Featured Image Link</label>
                <div className="relative">
                  <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                  <input
                    type="text"
                    name="image"
                    value={inputs.image}
                    onChange={handleChange}
                    placeholder="Enter image URL..."
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-sky-600 text-white font-black rounded-xl hover:bg-sky-700 active:scale-[0.98] transition-all shadow-lg shadow-sky-600/10 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-widest text-xs"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      Save Changes
                      <Edit3 size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-5 lg:sticky lg:top-8 self-start">
            <div className="flex items-center gap-2 mb-4 ml-2">
              <Eye size={16} className="text-sky-500" />
              <span className="text-[10px] font-black text-sky-950 uppercase tracking-widest">Live Visual Preview</span>
            </div>

            <div className="bg-gray-50/50 border border-gray-100 rounded-2xl overflow-hidden group">
              <div className="relative h-48 bg-white overflow-hidden">
                {inputs.image ? (
                  <img
                    src={inputs.image}
                    alt="Preview"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?q=80&w=1000&auto=format&fit=crop";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                    <ImageIcon size={32} />
                    <p className="text-[10px] font-black uppercase tracking-widest">No Image</p>
                  </div>
                )}
                {inputs.category && (
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur rounded-lg text-[9px] font-black uppercase tracking-widest text-sky-600 shadow-sm border border-white/50">
                    {inputs.category}
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-sky-500 mb-2">
                  <Globe size={12} />
                  <span className="text-[9px] font-black uppercase tracking-widest">
                    {inputs.country || "Select Province"}
                  </span>
                </div>
                <h2 className="text-xl font-black text-sky-950 mb-3 truncate">
                  {inputs.destinationName || "New Destination"}
                </h2>
                <p className="text-gray-400 text-xs font-bold leading-relaxed line-clamp-3 mb-6">
                  {inputs.description || "Destination description will appear here as you edit..."}
                </p>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Edit3 size={12} />
                    <span className="text-[9px] font-black uppercase tracking-widest">Editor Active</span>
                  </div>
                  <div className="w-8 h-1 bg-sky-200 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-sky-50 rounded-xl p-4 border border-sky-100/50">
              <p className="text-[10px] font-bold text-sky-800 leading-relaxed italic text-center">
                "Review the preview on the right to ensure the destination looks perfect for travelers before saving!"
              </p>
            </div>
          </div>

        </div>
      </div>
    </AdminDashboard>
  );
}

export default Updatedest;
